import React, { useContext, useState, useEffect } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { FilterData } from '../../../types/alerts';
import { DevicesContext } from '../../../contexts/DevicesContext';
import { DeviceDashboardDto } from '../../../services/deviceApi';

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (_filters: FilterData) => void;
  filters: FilterData;
}

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  onApplyFilters,
  filters
}) => {
  const [localFilters, setLocalFilters] = useState<FilterData>(filters);
  const [showDeviceDropdown, setShowDeviceDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  // Get user devices from API
  const { devicesForDashboard, loading: devicesLoading, error: devicesError, getDevicesForDashboard } = useContext(DevicesContext)!;
  const statuses = ['Active', 'Inactive'];

  useEffect(() => {
    if(devicesForDashboard.length === 0) {
      getDevicesForDashboard();
    }
  }, []);

  // Sync localFilters when filters prop changes (e.g., when modal opens)
  useEffect(() => {
    if (isOpen) {
      setLocalFilters(filters);
    }
  }, [filters, isOpen]);


  const handleApply = () => {
    onApplyFilters(localFilters);
    onClose();
  };

  const handleClear = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    const clearedFilters = { device: 'All Devices', status: 'All Statuses', date: '' };
    setLocalFilters(clearedFilters);
    onApplyFilters(clearedFilters);
    // Don't close the modal - just clear the filters
  };

  if (!isOpen) return null;

  return (
    <div className={`modal-overlay zoomOutLevel ${window.innerWidth <= 768 ? 'modal-filter-overlay' : ''}`} onClick={ () => window.innerWidth <= 768 && onClose()}>
      <div className={`modal-content ${window.innerWidth <= 768 ? 'mobile-filter-modal' : ''}`}>
        {/* Header */}
        <div className={`modal-header ${window.innerWidth <= 768 ? 'filter-modal-header' : ''} `}>
          <div>
            <h2 className="modal-title">Filter</h2>
          </div>
          <button onClick={onClose} className={`modal-close ${window.innerWidth <= 768 ? 'block' : ''}`}>
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Filter Form */}
        <div className="modal-form" onClick={e => e.stopPropagation()}>
          {/* Device Filter */}
          <div className="form-group">
            <label className="form-label">Device</label>
            <div className="relative">
              <button
                type="button"
                onClick={() => { setShowDeviceDropdown(!showDeviceDropdown), setShowStatusDropdown(false)} }
                className={`${localFilters.device && 'text-[var(--color-text)]'} form-dropdown-button ${showDeviceDropdown && 'active'}`}
                disabled={devicesLoading}
              >
                {devicesLoading ? 'Loading devices...' : (localFilters.device || 'Select Device')}
                <ChevronDown className="w-5 h-5 text-[var(--color-text)]" />
              </button>
              {showDeviceDropdown && !devicesLoading && (
                <div className="dropdown-menu">
                  <button
                    type="button"
                    onClick={() => {
                      setLocalFilters({ ...localFilters, device: 'All Devices' });
                      setShowDeviceDropdown(false);
                    }}
                    className="dropdown-item"
                  >
                    All Devices
                  </button>
                  {devicesError ? (
                    <div className="dropdown-item text-red-600">
                      Error loading devices: {devicesError}
                    </div>
                  ) : devicesForDashboard.length === 0 ? (
                    <div className="dropdown-item text-gray-500">
                      No devices found
                    </div>
                  ) : (
                    devicesForDashboard.map((device: DeviceDashboardDto) => (
                      <button
                        key={device.deviceId}
                        type="button"
                        onClick={() => {
                          setLocalFilters({ ...localFilters, device: device.deviceName || '' });
                          setShowDeviceDropdown(false);
                        }}
                        className="dropdown-item"
                      >
                        {device.deviceName}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            {devicesError && (
              <p className="text-red-600 text-sm mt-1">{devicesError}</p>
            )}
          </div>

          {/* Status Filter */}
          <div className="form-group">
            <label className="form-label">Status</label>
            <div className="relative">
              <button
                type="button"
                onClick={() => { setShowStatusDropdown(!showStatusDropdown), setShowDeviceDropdown(false)} }
                className={`${localFilters.status && 'text-[var(--color-text)]'} form-dropdown-button ${showStatusDropdown && 'active'}`}
              >
                {localFilters.status ? (localFilters.status.charAt(0).toUpperCase() + localFilters.status.slice(1)) : 'Select Status'}
                <ChevronDown className="w-5 h-5 text-[var(--color-text)]" />
              </button>
              {showStatusDropdown && (
                <div className="dropdown-menu">
                  <button
                    type="button"
                    onClick={() => {
                      setLocalFilters({ ...localFilters, status: 'All Statuses' });
                      setShowStatusDropdown(false);
                    }}
                    className="dropdown-item"
                  >
                    All Statuses
                  </button>
                  {statuses.map((status) => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => {
                        setLocalFilters({ ...localFilters, status: status.toLowerCase() });
                        setShowStatusDropdown(false);
                      }}
                      className="dropdown-item"
                    >
                      {status}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Date Filter */}
          <div className="form-group">
            <label className="form-label">Date</label>
            <div className="relative">
              <input
                type="date"
                value={localFilters.date || ''}
                onChange={(e) => setLocalFilters({ ...localFilters, date: e.target.value })}
                className="form-input"
                placeholder="Select Date"
              />
               {/* <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text)] w-5 h-5" /> */}
            </div>
          </div>

        </div>
        {/* Action Buttons */}
        <div className={`modal-actions md:pt-0 ${window.innerWidth <= 768 ? 'filter-modal-actions' : ''}`} onClick={e => e.stopPropagation()}>
          <button type="button" onClick={handleClear} className="modal-button secondary">
            Clear
          </button>
          <button type="button" onClick={handleApply} className="modal-button primary">
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterModal;
